// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/chats.test.tsx
import { type AskPunttChatScope } from "@mg/schemas/src/christo/askPuntt";
import { Callout, Flex, SegmentedControl, Text } from "@radix-ui/themes";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { askPunttChatNavLink } from "./utils/chatNavigation";

import { useAppSelector } from "../../utils/hooks";
import { useAskPunttAllChats } from "../../utils/queries/askPuntt";

export function formatChatDate(iso: string | undefined): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function AskPunttChats() {
  const [scope, setScope] = useState<AskPunttChatScope>("all");
  const { data, isLoading, isError } = useAskPunttAllChats({ scope });
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);
  const navigate = useNavigate();
  const [filterQuery, setFilterQuery] = useState<string>("");

  const chats = data?.chats ?? [];
  const total = data?.total ?? 0;

  const sortedChats = useMemo(
    () =>
      [...chats].sort(
        (a, b) =>
          new Date(b.createdAt ?? b.updatedAt).getTime() -
          new Date(a.createdAt ?? a.updatedAt).getTime(),
      ),
    [chats],
  );

  const filteredChats = useMemo(
    () =>
      filterQuery.trim()
        ? sortedChats.filter((c) =>
            c.title.toLowerCase().includes(filterQuery.toLowerCase()),
          )
        : sortedChats,
    [sortedChats, filterQuery],
  );

  const showAdminBanner =
    currentUserId !== undefined &&
    chats.some((c) => c.userId !== currentUserId);

  return (
    <div
      className="flex h-full flex-col overflow-y-auto"
      data-testid="ask-puntt-chats"
    >
      <div className="mx-auto w-full max-w-[960px] px-6 py-8">
        <Flex justify="between" align="center" className="mb-6">
          <Text size="7" weight="bold" className="text-puntt-neutral-gray-12">
            All Chats
          </Text>
          <SegmentedControl.Root
            value={scope}
            onValueChange={(v) => setScope(v as AskPunttChatScope)}
            size="2"
            aria-label="Filter chats by scope"
          >
            <SegmentedControl.Item
              value="mine"
              data-testid="ask-puntt-chats-filter-mine"
            >
              Mine
            </SegmentedControl.Item>
            <SegmentedControl.Item
              value="shared"
              data-testid="ask-puntt-chats-filter-shared"
            >
              Shared with me
            </SegmentedControl.Item>
            <SegmentedControl.Item
              value="all"
              data-testid="ask-puntt-chats-filter-all"
            >
              All
            </SegmentedControl.Item>
          </SegmentedControl.Root>
        </Flex>

        {showAdminBanner && (
          <Callout.Root
            color="blue"
            size="1"
            className="mb-4"
            data-testid="ask-puntt-chats-admin-banner"
          >
            <Callout.Text>
              You&apos;re viewing all chats for this enterprise as an admin.
            </Callout.Text>
          </Callout.Root>
        )}

        <div className="mb-4">
          <input
            type="text"
            placeholder="Filter chats..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            className="w-full rounded-md border border-puntt-neutral-gray-5 bg-base-white px-3 py-2 text-sm text-puntt-neutral-gray-12 placeholder:text-puntt-neutral-gray-9 focus:border-puntt-accent-7 focus:outline-none"
            data-testid="ask-puntt-chats-filter"
          />
        </div>

        {total > chats.length && (
          <Text
            size="1"
            className="mb-3 block text-puntt-neutral-gray-9"
            data-testid="ask-puntt-chats-total"
          >
            Showing {chats.length} of {total} chats
          </Text>
        )}

        {isLoading ? (
          <div
            className="flex flex-col gap-2"
            data-testid="ask-puntt-chats-skeleton"
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-12 animate-pulse rounded-md border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2"
              />
            ))}
          </div>
        ) : isError ? (
          <Callout.Root
            color="red"
            size="1"
            data-testid="ask-puntt-chats-error"
          >
            <Callout.Text>Failed to load chats. Please refresh.</Callout.Text>
          </Callout.Root>
        ) : filteredChats.length === 0 ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="4"
            className="mt-16"
          >
            <Text
              size="4"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              No chats yet
            </Text>
            <Text
              size="2"
              className="max-w-sm text-center text-puntt-neutral-gray-9"
            >
              Start a conversation with Ask Puntt to see it here.
            </Text>
          </Flex>
        ) : (
          <div
            className="flex flex-col gap-1"
            data-testid="ask-puntt-chats-list"
          >
            {filteredChats.map((chat) => (
              <button
                key={chat._id}
                type="button"
                className="flex w-full cursor-pointer items-center gap-3 rounded-md px-4 py-3 text-left transition-colors hover:bg-puntt-neutral-gray-3"
                onClick={() => navigate(askPunttChatNavLink(chat))}
                data-testid="ask-puntt-chat-row"
                data-chat-id={chat._id}
              >
                <div className="min-w-0 flex-1">
                  <Text
                    size="2"
                    weight="medium"
                    className="block truncate text-puntt-neutral-gray-12"
                  >
                    {chat.title}
                  </Text>
                  {chat.userId !== currentUserId && chat.ownerName ? (
                    <Text
                      size="1"
                      className="block truncate text-puntt-neutral-gray-9"
                      data-testid="ask-puntt-chat-row-owner"
                    >
                      owned by {chat.ownerName}
                    </Text>
                  ) : null}
                </div>
                <Text size="1" className="shrink-0 text-puntt-neutral-gray-9">
                  {formatChatDate(chat.createdAt ?? chat.updatedAt)}
                </Text>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
